<template>
  <BaseGraph v-bind="$attrs"
             v-on="$listeners"
             :containerStyle="containerStyle"
             :disablePopover="disablePopover"
             :graphStyle="graphStyle"
             :extraIndex="extraIndex"
             :height="height"
             :isHighlighted="isHighlighted"
             :labelMaxLength="labelMaxLength"
             :links="links"
             :nodes="nodes"
             :popover="popover"
             :triggerEdgeUpdates="triggerEdgeUpdates"
             :triggerFitView="triggerFitView"
             :triggerNodeUpdates="triggerNodeUpdates"
             :width="width" />
</template>

<script>
  import BaseGraph from '@base/base-graph'
  import { FontStyle, Layout } from '@js/styles'
  import { mapGetters } from 'vuex'
  import { skillNodeRadiusFunctions } from '@js/skill-utils'

  export default {
    name: 'SkillGraphPrimary',
    inheritAttrs: false,
    components: {
      BaseGraph,
    },
    props: {
      disableConditionalPopover: { type: Boolean, required: false, default: false },
      disablePopover: { type: Boolean, required: false, default: false },
      extraIndex: { type: String, required: false, default: '0' },
      height: { type: Number, required: false, default: 480 },
      isHighlighted: { type: Function, required: false, default: (node) => { return false; } },
      labelMaxLength: { type: Number, required: false, default: 16 },
      links: { type: Array, required: true },
      nodeRadiusFunction: {
        type: Function,
        required: false,
        default: (node) => skillNodeRadiusFunctions.byPeriod(node, 3, 5, 24),
      },
      nodes: { type: Array, required: true },
      triggerEdgeUpdates: { type: Boolean, required: false, default: false },
      triggerFitView: { type: Boolean, required: false, default: false },
      triggerNodeUpdates: { type: Boolean, required: false, default: false },
      width: { type: Number, required: false, default: 640 },
    },
    data: function () {
      return {
        containerStyle: {
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          border: 'solid 1px transparent !important',
          borderRadius: '8px',
        },
        graphStyle: {
          cooldownTicks: 20,
          cursorOnHover: 'pointer',
          defaultLabelAngle: 90.0,
          distanceMax: 60.0,
          fitDuration: 200,
          fitPadding: 40,
          fontFamily: FontStyle.family.primary,
          hoverWithNeighbors: true,
          initCooldownTicks: 0,
          initWarmupTicks: 50,
          labelMargin: 2,
          linkWidth: 2,
          linkHoverPrecision: 40,
          maxZoom: 20.0,
          nodeId: 'id',
          nodeRadius: 3,
          nodeRadiusFunction: this.nodeRadiusFunction,
          warmupTicks: 0,
        },
      };
    },
    created: function () {
      this.containerStyle = {
        ...this.containerStyle,
        ...this.$attrs,
      };

      this.graphStyle = {
        ...this.graphStyle,
        fontColor: {
          'default': this.color.dark2(),
          hover: null,
          hoverNeighbor: null,
          highlight: null,
        },
        fontSize: {
          'default': FontStyle.size.sm,
          hover: null,
          hoverNeighbor: null,
          highlight: null,
        },
        fontWeight: {
          'default': FontStyle.weight.md,
          hover: null,
          hoverNeighbor: null,
          highlight: null,
        },
        nodeColor: {
          'default': this.color.dark2(),
          hover: this.color.theme1(),
          hoverNeighbor: this.color.theme1(),
          highlight: this.color.highlight2(),
        },
        ringColor: {
          'default': this.color.dark4(),
          hover: this.color.theme2(),
          hoverNeighbor: this.color.theme2(),
          highlight: this.color.highlight3(),
        },
        ringWidth: {
          'default': 3,
          hover: 3,
          hoverNeighbor: 3,
          highlight: 3,
        },
      };
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      popover: function (node) {
        const commonStyle = `border-radius: ${Layout.space.sm};
                             box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                             font-family: ${FontStyle.family.primary};
                             font-size: ${FontStyle.size.sm};
                             font-weight: ${FontStyle.weight.md};
                             margin: -${Layout.space.sm};
                             min-width: 120px;
                             padding: ${Layout.space.sm} ${Layout.space.md};`;
        const styles = {
          'default': `background-color: ${this.color.light1()};
                      color: ${this.color.dark2()};` + commonStyle,
          'highlight': `background-color: ${this.color.overlayBorder1()};
                        color: ${this.color.light1()};` + commonStyle,
        };
        const nameStyle = `font-weight: ${FontStyle.weight.xl}`;
        const labelStyle = `color: ${this.color.dark3()}`;

        return node => {
          const highlight = !this.disableConditionalPopover && this.isHighlighted(node);
          const style = highlight ? styles.highlight : styles.default;

          const htmlPrefix = `
            <div style="${style}">
              <div style="${nameStyle}">
                ${node.name}
              </div>`;
          const htmlPostfix = `</div>`;

          const html = highlight ? `
            ${htmlPrefix}
              <div>
                Added by others
              </div>
            ${htmlPostfix}` : `
              ${htmlPrefix}
              <div>
                <span style="${labelStyle}">Status:</span> ${node.skillStatusType ? node.skillStatusType.name : 'N/A'}
              </div>
              <div>
                <span style="${labelStyle}">Experience:</span> ${node.experiencePeriodCategory ? node.experiencePeriodCategory.name : 'N/A'}
              </div>
            ${htmlPostfix}`;
          return html;
        };
      },
    },
  };
</script>
